import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { userService, CreateUserDto } from "../../services/user.service";
import { storeService, Store } from "../../services/store.service";
import { rbacService, RoleRecord } from "../../services/rbac.service";
import { useAuthStore } from "../../stores/authStore";
import { showToast } from "../../lib/toast";

export function CreateUserPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [formData, setFormData] = useState<CreateUserDto>({
    tenantId: user?.tenantId || "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
    roleId: "",
    storeIds: [],
  });

  useEffect(() => {
    loadStores();
    loadRoles();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchUser();
    }
  }, [id]);

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data || []);
    } catch (err) {
      setStores([]);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await rbacService.getRoles();
      const tenantRoles = data.filter(
        (role) => role.tenantId === user?.tenantId,
      );
      setRoles(tenantRoles);
      setFormData((prev) => ({
        ...prev,
        roleId: prev.roleId || tenantRoles[0]?.id || "",
      }));
    } catch (err) {
      setRoles([]);
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const data = await userService.getUser(id!);
      setFormData({
        tenantId: data.tenantId,
        email: data.email,
        password: "",
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        roleId: data.roleId || "",
        storeIds: data.userStores?.map((us) => us.store.id) || [],
      });
      setSelectedStores(data.userStores?.map((us) => us.store.id) || []);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      showToast.error("Failed to fetch user details");
      navigate("/users");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleStore = (storeId: string) => {
    setSelectedStores((prev) => {
      const exists = prev.includes(storeId);
      const next = exists
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId];
      setFormData((current) => ({
        ...current,
        storeIds: next,
      }));
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.tenantId) {
      showToast.error("Tenant is required");
      return;
    }
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      showToast.error("First and last name are required");
      return;
    }
    if (!formData.email.trim()) {
      showToast.error("Email is required");
      return;
    }
    if (!formData.phone.trim()) {
      showToast.error("Phone is required");
      return;
    }
    if (!formData.roleId) {
      showToast.error("Role is required");
      return;
    }
    if (!isEditMode && !formData.password.trim()) {
      showToast.error("Password is required");
      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        const payload = { ...formData };
        if (!payload.password) {
          delete (payload as any).password;
        }
        await userService.updateUser(id!, payload);
        showToast.success("User updated successfully");
      } else {
        await userService.createUser(formData);
        showToast.success("User created successfully");
      }
      navigate("/users");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} user`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/users")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Users
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? "Edit User" : "Add New User"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="roleId"
                value={formData.roleId}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              {roles.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No roles available. Please create roles first.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {isEditMode ? "" : "*"}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isEditMode ? "Leave blank to keep current" : ""}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Store Assignment */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Store Assignment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stores.map((store) => (
              <label
                key={store.id}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={selectedStores.includes(store.id)}
                  onChange={() => toggleStore(store.id)}
                />
                {store.name}
              </label>
            ))}
          </div>
          {stores.length === 0 && (
            <div className="text-sm text-gray-500">
              No stores available to assign.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/users")}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : isEditMode ? "Update User" : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
}
